import { Head, Link, useForm } from '@inertiajs/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Send } from 'lucide-react'
import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { TextArea } from '@/components/ui/TextArea'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'
import { cn } from '@/lib/cn'
import { useChatroomChannel, type ChatroomPayload } from '@/lib/useChatroomChannel'
import { FieldError } from '@/components/feedback/FieldError'

export type TeamChatroomShowProps = {
  chatroom: {
    id: string
    playerName: string
    counterpartName: string
  }
  messages: Array<{
    id: string
    content: string
    createdAt: string
    senderName: string
    senderType: 'Player' | 'SchoolCoach' | 'TeamCoach'
  }>
  breadcrumbs: Breadcrumb[]
}

export default function TeamChatroomShow({ chatroom, messages, breadcrumbs }: TeamChatroomShowProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [chatMessages, setChatMessages] = useState(messages)
  const { data, setData, post, processing, errors, transform } = useForm({
    content: '',
  })

  useEffect(() => {
    setChatMessages(messages)
  }, [messages])

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight
    }
  }, [chatMessages])

  const handleBroadcast = useCallback((payload: ChatroomPayload) => {
    setChatMessages((prev) => {
      if (prev.some((message) => message.id === payload.message.id)) {
        return prev
      }

      return [
        ...prev,
        {
          ...payload.message,
        },
      ]
    })
  }, [])

  useChatroomChannel(chatroom.id, handleBroadcast)

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!data.content.trim()) return

    transform((data) => ({ chat_message: data }))
    post(`/team/chatrooms/${chatroom.id}/messages`, {
      preserveScroll: true,
      onSuccess: () => setData('content', ''),
    })
  }

  return (
    <AppShell>
      <Head title={`${chatroom.playerName} | チャット`} />
      <PageHeader
        title={chatroom.playerName}
        description={chatroom.counterpartName}
        breadcrumbs={breadcrumbs}
        actions={
          <Link href="/team/chatrooms" className="text-sm text-brand-300 hover:text-brand-100 transition">
            チャット一覧へ戻る
          </Link>
        }
      />

      <Card className="h-[calc(100vh-16rem)] flex flex-col">
        <div ref={viewportRef} className="flex-1 overflow-y-auto space-y-4 bg-white/5 px-4 py-6 rounded-2xl">
          {chatMessages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-sm text-white/60">
              <p>まだメッセージはありません。</p>
            </div>
          ) : (
            chatMessages.map((message) => {
              const isPlayer = message.senderType === 'Player'
              const isTeamCoach = message.senderType === 'TeamCoach'
              return (
                <div key={message.id} className={`flex ${isPlayer || isTeamCoach ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex max-w-[75%] flex-col ${isPlayer || isTeamCoach ? 'items-end' : 'items-start'}`}>
                    <span className="mb-1 text-[11px] text-white/50">{message.senderName}</span>
                    <div
                      className={cn(
                        'rounded-2xl px-4 py-2 text-sm shadow-soft',
                        isPlayer || isTeamCoach ? 'bg-brand-500 text-white' : 'bg-white/10 text-white'
                      )}
                    >
                      <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                    </div>
                    <time className="mt-1 text-[11px] text-white/40">{formatDate(message.createdAt)}</time>
                  </div>
                </div>
              )
            })
          )}
        </div>

        <form onSubmit={submit} className="border-t border-white/5 bg-surface-900/80 px-4 py-4">
          <div className="flex items-center gap-3">
            <TextArea
              autoResize
              value={data.content}
              onChange={(event) =>
                setData('content', event.target.value)
              }
              placeholder="メッセージを入力..."
              className="flex-1 resize-none"
              rows={2}
            />
            <Button type="submit" disabled={processing || !data.content.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <FieldError message={errors.content} />
        </form>
      </Card>
    </AppShell>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
